import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../AuthContext';
import BillCaptureField from './BillCaptureField';
import SearchableSelect from './SearchableSelect';

/**
 * Ledger Entry Form — mirrors the Expenditure entry form (Project Activity +
 * Block + Expenditure Head + Amount + Ref No + Description) with an added
 * Income / Expenditure toggle so it can double as a Debit or Credit line.
 *
 * Used by both MemberLedger and BranchLedger.
 *
 * Props:
 *   editing      – set when editing an existing entry
 *   initial      – initial form values (for editing)
 *   lockedBranchId – if provided (e.g. Branch Ledger), disables the block picker
 *   onSubmit(payload) – async submit handler returning success
 *   onCancel     – close the dialog
 */
const emptyForm = {
  entry_type: 'expenditure',
  date: new Date().toISOString().split('T')[0],
  project_activity_id: '',
  branch_id: '',
  expenditure_head_id: '',
  head_name: '',
  amount: '',
  reference_no: '',
  description: '',
  bill_document: null,
  bill_filename: null,
};

const LedgerEntryForm = ({ editing, initial, lockedBranchId, allowBill = false, onSubmit, onCancel }) => {
  const { user, isAdmin } = useAuth();
  const [form, setForm] = useState({ ...emptyForm, ...(initial || {}) });
  const [bill, setBill] = useState(null); // { dataUrl, filename }
  const [projectActivities, setProjectActivities] = useState([]);
  const [expenditureHeads, setExpenditureHeads] = useState([]);
  const [branches, setBranches] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const extraAllowed = Array.isArray(user?.allowed_branch_ids) ? user.allowed_branch_ids : [];
  const canViewAll = isAdmin() || ['Checker', 'Account', 'HR'].includes(user?.role);
  const canSelectBranch = !lockedBranchId && (canViewAll || extraAllowed.length > 0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [pa, eh, lh, br] = await Promise.all([
          api.get('/project-activities'),
          api.get('/expenditure-heads'),
          api.get('/ledger/heads'),
          api.get('/branches'),
        ]);
        if (!mounted) return;
        setProjectActivities(pa.data || []);
        // Merge both head masters (Expenditure Heads + Manage Heads / Ledger Heads),
        // de-duped by lowercase name. Expenditure Heads take precedence so their real
        // id is preserved for legacy cross-reference; new-from-ledger-heads get their
        // own id (backend accepts head_name and auto-creates in ledger_heads if missing).
        const map = new Map();
        (eh.data || []).forEach((h) => {
          const key = (h.name || '').trim().toLowerCase();
          if (!key) return;
          map.set(key, { id: h.id, name: h.name, source: 'expenditure' });
        });
        (lh.data || []).forEach((h) => {
          const key = (h.name || '').trim().toLowerCase();
          if (!key || map.has(key)) return;
          map.set(key, { id: h.id, name: h.name, source: 'ledger' });
        });
        setExpenditureHeads(Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)));
        setBranches(br.data || []);
      } catch {
        /* soft-fail — user still sees empty dropdowns */
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Default branch: locked prop wins, otherwise user's home branch
  useEffect(() => {
    if (lockedBranchId) {
      setForm((f) => ({ ...f, branch_id: lockedBranchId }));
    } else if (!form.branch_id && user?.branch_id) {
      setForm((f) => ({ ...f, branch_id: user.branch_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedBranchId, user?.branch_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_activity_id) { toast.error('Select a Project Activity'); return; }
    if (!form.expenditure_head_id) { toast.error('Select an Expenditure Head'); return; }
    if (!form.branch_id) { toast.error('Select a Block / Branch'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount must be > 0'); return; }
    const headObj = expenditureHeads.find((h) => h.id === form.expenditure_head_id);
    const payload = {
      ...form,
      amount: Number(form.amount),
      head_name: headObj?.name || 'Expenditure',
    };
    if (allowBill && bill?.dataUrl) {
      payload.bill_document = bill.dataUrl;
      payload.bill_filename = bill.filename;
    }
    setSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const visibleBranches = canViewAll
    ? branches
    : branches.filter((b) => b.id === user?.branch_id || extraAllowed.includes(b.id));

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="ledger-entry-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Entry Type *</Label>
          <Select value={form.entry_type} onValueChange={(v) => setForm({ ...form, entry_type: v })}>
            <SelectTrigger data-testid="entry-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="expenditure">Expenditure (Debit)</SelectItem>
              <SelectItem value="income">Income (Credit)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date *</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="entry-date" />
        </div>
      </div>

      <div>
        <Label>Project Activity *</Label>
        <SearchableSelect
          value={form.project_activity_id}
          onChange={(v) => setForm({ ...form, project_activity_id: v })}
          options={projectActivities.map((p) => ({ value: p.id, label: p.name }))}
          placeholder="Select Project Activity — type to search"
          testId="entry-project-activity"
        />
      </div>

      <div>
        <Label>Block / Branch *</Label>
        {lockedBranchId ? (
          <Input value={visibleBranches.find((b) => b.id === lockedBranchId)?.name || lockedBranchId} readOnly disabled data-testid="entry-branch-locked" />
        ) : canSelectBranch ? (
          <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
            <SelectTrigger data-testid="entry-branch"><SelectValue placeholder="Select Block" /></SelectTrigger>
            <SelectContent>
              {visibleBranches.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}{b.id === user?.branch_id ? ' (Home)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input value={visibleBranches.find((b) => b.id === user?.branch_id)?.name || user?.branch_id || '—'} readOnly disabled data-testid="entry-branch-readonly" />
        )}
      </div>

      <div>
        <Label>Head <span className="text-xs text-slate-500 font-normal">(Expenditure / Payment)</span> *</Label>
        <SearchableSelect
          value={form.expenditure_head_id}
          onChange={(v) => setForm({ ...form, expenditure_head_id: v })}
          options={expenditureHeads.map((h) => ({ value: h.id, label: h.name }))}
          placeholder="Select Head — type to search"
          testId="entry-head"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount (Rs.) *</Label>
          <Input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="entry-amount" />
        </div>
        <div>
          <Label>Reference No.</Label>
          <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="Voucher / receipt no" data-testid="entry-ref" />
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>

      {allowBill && (
        <BillCaptureField value={bill} onChange={setBill} disabled={submitting} />
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-entry-btn">
          {editing ? 'Update Entry' : 'Post Entry'}
        </Button>
      </div>
    </form>
  );
};

export default LedgerEntryForm;
